import { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Plus, Search, Edit, Trash2, Calculator, Info, Loader2, Save,
} from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const fmtR = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const Pricing = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateDialogPrefill, setRateDialogPrefill] = useState(null);

  // Calculator state
  const [calcRate, setCalcRate] = useState(900);
  const [calcHours, setCalcHours] = useState(2);
  const [calcMinutes, setCalcMinutes] = useState(35);

  // Table filters
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [showInactiveServices, setShowInactiveServices] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get('/services', { params: { include_inactive: true } }),
        api.get('/customers'),
      ]);
      setServices(s.data || []);
      setCustomers(c.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const serviceById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);

  // Flatten customer.pricing[] into table rows
  const pricingRows = useMemo(() => {
    const rows = [];
    customers.forEach((cust) => {
      (cust.pricing || []).forEach((p) => {
        rows.push({
          customer_id: cust.id,
          customer_name: cust.company_name,
          customer_code: cust.code,
          service_id: p.service_id,
          service_name: serviceById[p.service_id]?.name || '—',
          rate: p.rate,
          minimum_units: p.minimum_units,
          billing_model: p.billing_model || 'standard',
          base_fee: p.base_fee,
          included_hours: p.included_hours,
          overflow_rate: p.overflow_rate,
          effective_from: p.effective_from,
          active: cust.active,
        });
      });
    });
    return rows;
  }, [customers, serviceById]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pricingRows.filter((r) => {
      if (serviceFilter !== 'all' && r.service_id !== serviceFilter) return false;
      if (!q) return true;
      return r.customer_name.toLowerCase().includes(q) ||
             (r.customer_code || '').toLowerCase().includes(q) ||
             r.service_name.toLowerCase().includes(q);
    });
  }, [pricingRows, search, serviceFilter]);

  const calcSubtotal = useMemo(() => {
    const hours = Number(calcHours || 0) + (Number(calcMinutes || 0) / 60);
    return Number(calcRate || 0) * hours;
  }, [calcRate, calcHours, calcMinutes]);
  const calcTotal = useMemo(() => calcSubtotal * 1.15, [calcSubtotal]);

  if (loading) {
    return (
      <Layout title="Pricing Configuration" subtitle="Service Rates & Billing Rules">
        <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading pricing data…
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Pricing Configuration" subtitle="Service Rates & Billing Rules">
      <div className="space-y-6 animate-fade-in" data-testid="pricing-page">
        {/* Info banner */}
        <Card className="bg-[#80C030]/5 border-[#80C030]/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#80C030] rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-medium text-[#0D5B69]">All prices are exclusive of VAT</p>
                <p className="text-sm text-[#0D5B69]/80 mt-1">
                  VAT at 15% is applied during billing. Each customer can override these defaults below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Default Service Rates (live) */}
        <div>
          <div className="flex items-end justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">Default Service Rates</h3>
            <div className="flex items-center gap-4">
              {isAdmin && services.some((s) => !s.active) && (
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="show-inactive-services-toggle">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-[#80C030]"
                    checked={showInactiveServices}
                    onChange={(e) => setShowInactiveServices(e.target.checked)}
                  />
                  Show inactive
                  <span className="text-[10px] text-slate-400">
                    ({services.filter((s) => !s.active).length})
                  </span>
                </label>
              )}
              <p className="text-xs text-slate-500">
                {isAdmin ? 'Click any card to edit. Customer overrides apply below.' : 'Read-only — admin can edit.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {services
              .filter((s) => (showInactiveServices ? true : s.active))
              .map((svc) => (
              <Card
                key={svc.id}
                className={`bg-white border-slate-200 ${isAdmin ? 'cursor-pointer hover:border-[#80C030] hover:shadow-sm' : ''} transition ${svc.active ? '' : 'opacity-60'}`}
                onClick={() => isAdmin && setEditingService({ ...svc })}
                data-testid={`default-rate-${svc.code}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-xs text-slate-500 truncate flex-1">{svc.name}</p>
                    {!svc.active && (
                      <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100 text-[9px] py-0 px-1.5">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-xl font-bold font-heading text-[#0D5B69]">{fmtR(svc.default_rate)}</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {svc.billing_type === 'fixed' ? 'per call (fixed)' : `per hour · min ${svc.minimum_units || 0}h`}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Quick Billing Calculator (reactive) */}
        <Card className="bg-white border-slate-200" data-testid="billing-calculator">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#80C030]" />
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Quick Billing Calculator</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Hourly Rate (R)</Label>
                <Input type="number" min={0} value={calcRate} onChange={(e) => setCalcRate(e.target.value)} data-testid="calc-rate" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Hours</Label>
                <Input type="number" min={0} value={calcHours} onChange={(e) => setCalcHours(e.target.value)} data-testid="calc-hours" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Minutes</Label>
                <Input type="number" min={0} max={59} value={calcMinutes} onChange={(e) => setCalcMinutes(e.target.value)} data-testid="calc-minutes" />
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Subtotal (excl. VAT)</p>
                <p className="text-lg font-bold text-slate-900" data-testid="calc-subtotal">{fmtR(calcSubtotal)}</p>
              </div>
              <div className="bg-[#80C030]/10 rounded-lg p-3 border border-[#80C030]/30">
                <p className="text-xs text-[#0D5B69] uppercase tracking-wide">Total (incl. 15% VAT)</p>
                <p className="text-lg font-bold text-[#0D5B69]" data-testid="calc-total">{fmtR(calcTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer-Specific Pricing (live) */}
        <Card className="bg-white border-slate-200" data-testid="pricing-table-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Customer-Specific Pricing</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="text" placeholder="Search pricing..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 w-56" data-testid="pricing-search" />
                </div>
                <Select value={serviceFilter} onValueChange={setServiceFilter}>
                  <SelectTrigger className="w-52" data-testid="service-filter"><SelectValue placeholder="Service Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Button
                    onClick={() => { setRateDialogPrefill(null); setRateDialogOpen(true); }}
                    className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
                    data-testid="add-pricing-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Rate
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs">Service</TableHead>
                  <TableHead className="text-xs text-right">Hourly Rate</TableHead>
                  <TableHead className="text-xs text-right">Min Units (h)</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No customer-specific pricing rules. {isAdmin && 'Click "Add Rate" to create one.'}</TableCell></TableRow>
                ) : filteredRows.map((r, idx) => (
                  <TableRow key={`${r.customer_id}-${r.service_id}`} className="hover:bg-slate-50/80" data-testid={`pricing-row-${idx}`}>
                    <TableCell>
                      <p className="font-medium text-sm text-slate-900">{r.customer_name}</p>
                      <p className="text-xs text-slate-500">{r.customer_code || '—'}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{r.service_name}</TableCell>
                    <TableCell className="text-right">
                      {r.billing_model === 'flat_plus_hourly' ? (
                        <div>
                          <p className="font-mono text-sm font-semibold">{fmtR(r.base_fee)} flat</p>
                          <p className="text-[11px] text-slate-500">
                            first {r.included_hours || 0}h, then {fmtR(r.overflow_rate)}/h
                            {r.effective_from ? ` · from ${r.effective_from}` : ''}
                          </p>
                        </div>
                      ) : (
                        <span className="font-mono text-sm font-semibold">{fmtR(r.rate)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-600">
                      {r.billing_model === 'flat_plus_hourly' ? '—' : (r.minimum_units || '—')}
                    </TableCell>
                    <TableCell>
                      <Badge className={r.active ? 'bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30' : 'bg-slate-100 text-slate-500'}>
                        {r.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setRateDialogPrefill(r); setRateDialogOpen(true); }} data-testid={`edit-pricing-${idx}`}>
                              <Edit className="w-4 h-4 text-slate-500" />
                            </Button>
                            <DeleteRateButton row={r} customers={customers} onDone={load} testid={`delete-pricing-${idx}`} />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialogs */}
        {editingService && (
          <EditServiceRateDialog
            service={editingService}
            onClose={() => setEditingService(null)}
            onSaved={() => { setEditingService(null); load(); }}
          />
        )}
        <AddEditRateDialog
          open={rateDialogOpen}
          setOpen={setRateDialogOpen}
          prefill={rateDialogPrefill}
          customers={customers}
          services={services}
          onSaved={load}
        />
      </div>
    </Layout>
  );
};


const EditServiceRateDialog = ({ service, onClose, onSaved }) => {
  const [rate, setRate] = useState(service.default_rate);
  const [minUnits, setMinUnits] = useState(service.minimum_units || 0);
  const [active, setActive] = useState(service.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/services/${service.id}`, {
        default_rate: Number(rate),
        minimum_units: Number(minUnits),
        active,
      });
      toast.success(`${service.name} updated`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0D5B69]">Edit Default Rate — {service.name}</DialogTitle>
          <DialogDescription>This rate applies to any customer who does NOT have a customer-specific override.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Default Rate (R, excl. VAT)</Label>
            <Input type="number" min={0} step={0.01} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="edit-service-rate-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">
              Minimum Units (hours){service.billing_type === 'fixed' ? ' — N/A for fixed' : ''}
            </Label>
            <Input type="number" min={0} step={0.25} value={minUnits} onChange={(e) => setMinUnits(e.target.value)} disabled={service.billing_type === 'fixed'} data-testid="edit-service-min-input" />
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {active ? 'Service is active and selectable.' : 'Service is hidden from new incidents and pricing.'}
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="edit-service-active-toggle">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#80C030]"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <span className="text-xs font-semibold text-slate-700">{active ? 'Active' : 'Inactive'}</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="save-service-rate-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const AddEditRateDialog = ({ open, setOpen, prefill, customers, services, onSaved }) => {
  const [form, setForm] = useState({
    customer_id: '', service_id: '', rate: '', minimum_units: 0,
    billing_model: 'standard', base_fee: '', included_hours: '', overflow_rate: '',
    effective_from: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        customer_id: prefill?.customer_id || (customers[0]?.id || ''),
        service_id: prefill?.service_id || (services[0]?.id || ''),
        rate: prefill?.rate ?? '',
        minimum_units: prefill?.minimum_units || 0,
        billing_model: prefill?.billing_model || 'standard',
        base_fee: prefill?.base_fee ?? '',
        included_hours: prefill?.included_hours ?? '',
        overflow_rate: prefill?.overflow_rate ?? '',
        effective_from: prefill?.effective_from || new Date().toISOString().slice(0, 10),
      });
    }
  }, [open, prefill, customers, services]);

  const isEdit = !!prefill;
  const isFlat = form.billing_model === 'flat_plus_hourly';

  const save = async () => {
    if (!form.customer_id || !form.service_id) { toast.error('Pick a customer and service'); return; }
    let entry;
    if (isFlat) {
      const baseFee = Number(form.base_fee);
      const overflowRate = Number(form.overflow_rate);
      const includedHours = Number(form.included_hours);
      if (!baseFee || baseFee <= 0) { toast.error('Flat base fee must be greater than zero'); return; }
      if (!includedHours || includedHours <= 0) { toast.error('Included hours must be greater than zero'); return; }
      if (!overflowRate || overflowRate < 1) { toast.error('Hourly Rate is too low (must be at least R1). This rate also bills cases opened before the effective date — enter your real rate, e.g. R700.'); return; }
      entry = {
        service_id: form.service_id,
        rate: overflowRate, // kept in sync for legacy displays
        minimum_units: 0,
        billing_model: 'flat_plus_hourly',
        base_fee: baseFee,
        included_hours: includedHours,
        overflow_rate: overflowRate,
        effective_from: form.effective_from || new Date().toISOString().slice(0, 10),
      };
    } else {
      const numericRate = Number(form.rate);
      if (!numericRate || numericRate <= 0) { toast.error('Rate must be greater than zero'); return; }
      entry = {
        service_id: form.service_id, rate: numericRate,
        minimum_units: Number(form.minimum_units || 0), billing_model: 'standard',
      };
    }

    setSaving(true);
    try {
      const cust = customers.find((c) => c.id === form.customer_id);
      const existing = (cust.pricing || []).filter((p) => p.service_id !== form.service_id);
      await api.patch(`/customers/${form.customer_id}`, { pricing: [...existing, entry] });
      toast.success(isEdit ? 'Rate updated' : 'Rate added');
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0D5B69]">{isEdit ? 'Edit Customer Rate' : 'Add Customer Rate'}</DialogTitle>
          <DialogDescription>Overrides the default service rate for this customer only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Customer</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v }))} disabled={isEdit}>
              <SelectTrigger data-testid="rate-customer-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.filter((c) => c.active).map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Service</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))} disabled={isEdit}>
              <SelectTrigger data-testid="rate-service-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {services.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Billing Model</Label>
            <Select value={form.billing_model} onValueChange={(v) => setForm((f) => ({ ...f, billing_model: v }))}>
              <SelectTrigger data-testid="rate-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard — hourly rate × time</SelectItem>
                <SelectItem value="flat_plus_hourly">Flat fee + hourly after included hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isFlat ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Flat Base Fee (R)</Label>
                  <Input type="number" min={0} step={0.01} value={form.base_fee} onChange={(e) => setForm((f) => ({ ...f, base_fee: e.target.value }))} placeholder="e.g. 1000" data-testid="rate-base-fee-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Included Hours</Label>
                  <Input type="number" min={0} step={0.25} value={form.included_hours} onChange={(e) => setForm((f) => ({ ...f, included_hours: e.target.value }))} placeholder="e.g. 3" data-testid="rate-included-hours-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Hourly Rate (R)</Label>
                  <Input type="number" min={0} step={0.01} value={form.overflow_rate} onChange={(e) => setForm((f) => ({ ...f, overflow_rate: e.target.value }))} placeholder="e.g. 700" data-testid="rate-overflow-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Effective From (flat fee applies to incidents opened on/after)</Label>
                <Input type="date" value={form.effective_from} onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} data-testid="rate-effective-from-input" />
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="rate-historical-warning">
                  <strong>Important:</strong> the flat fee only applies from this date. Cases opened <strong>before</strong> it bill at the <strong>Hourly Rate × their hours</strong>. Set the Hourly Rate to your real standard rate (e.g. R700) — a placeholder like R0.01 will zero out every historical bill.
                </p>
              </div>
              {Number(form.overflow_rate) > 0 && Number(form.overflow_rate) < 1 && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" data-testid="rate-low-hourly-warning">
                  ⚠ The Hourly Rate is set to {fmtR(form.overflow_rate)}. This will make any case opened before the effective date bill at nearly R0.00. Did you mean R700.00?
                </div>
              )}
              {Number(form.base_fee) > 0 && Number(form.included_hours) > 0 && Number(form.overflow_rate) > 0 && (
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="rate-flat-preview">
                  Example: a {Number(form.included_hours) + 1}h20m job bills {fmtR(Number(form.base_fee) + (1 + 20 / 60) * Number(form.overflow_rate))}
                  &nbsp;({fmtR(form.base_fee)} flat + 1h20m × {fmtR(form.overflow_rate)})
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Hourly Rate (R, excl. VAT)</Label>
                <Input type="number" min={0} step={0.01} value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))} placeholder="e.g. 950" data-testid="rate-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Min Units (hours)</Label>
                <Input type="number" min={0} step={0.25} value={form.minimum_units} onChange={(e) => setForm((f) => ({ ...f, minimum_units: e.target.value }))} data-testid="rate-min-input" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="save-rate-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isEdit ? 'Update' : 'Add Rate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const DeleteRateButton = ({ row, customers, onDone, testid }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!window.confirm(`Remove ${row.service_name} rate for ${row.customer_name}? Default service rate will apply.`)) return;
    setBusy(true);
    try {
      const cust = customers.find((c) => c.id === row.customer_id);
      const next = (cust.pricing || []).filter((p) => p.service_id !== row.service_id);
      await api.patch(`/customers/${row.customer_id}`, { pricing: next });
      toast.success('Rate removed — default applies');
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handle} disabled={busy} data-testid={testid}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Trash2 className="w-4 h-4 text-red-500" />}
    </Button>
  );
};
